// Node Editor Modal Component
import { useEffect, useState, useMemo, useCallback } from 'react';
import { X, Play, Sparkles, Database } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useWorkflowStore } from '@/stores/workflow-store';
import { NodeInputPanel } from './NodeInputPanel';
import { DynamicNodeParametersPanel } from './DynamicNodeParametersPanel';
import { NodeOutputPanel } from './NodeOutputPanel';
import { CodeAgentPanel } from './CodeAgentPanel';
import { NavigationButtons, SubNodeNavigationButtons } from './NavigationButton';
import { InputPanelSkeleton, OutputPanelSkeleton, PropertyPanelSkeleton } from './LoadingSkeletons';
import { workflowApi } from '@/lib/api';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '@/components/ui/resizable';
import { getNodeDefinition } from '@/utils/nodeDefinitions';
import { toast } from 'sonner';
import { workflowGraphService } from '@/services/workflow-graph.service';
import { executionApi } from '@/lib/api';
import { NodeEditorErrorBoundary } from '@/components/error/NodeEditorErrorBoundary';
import { NodeTestingService } from '@/services/node-testing.service';
import { NodeNavigationService } from '@/services/node-navigation.service';

interface NodeEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  nodeId: string;
  workflowId: string;
  onNavigate?: (nodeId: string) => void;
}

export function NodeEditorModal({ isOpen, onClose, nodeId, workflowId, onNavigate }: NodeEditorModalProps) {
  const { nodes, edges, updateNode } = useWorkflowStore();
  const [isTesting, setIsTesting] = useState(false);
  const [testOutput, setTestOutput] = useState<any>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [currentNodeId, setCurrentNodeId] = useState(nodeId);
  const [isExecutingPrevious, setIsExecutingPrevious] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isInitializing, setIsInitializing] = useState(true);
  const [leftPanelTab, setLeftPanelTab] = useState<'input' | 'ai-help'>('input');

  // Sync currentNodeId with nodeId prop when it changes
  useEffect(() => {
    if (nodeId) {
      setCurrentNodeId(nodeId);
      setIsInitializing(true);
      // Show skeleton briefly for smooth transition
      const timer = setTimeout(() => setIsInitializing(false), 300);
      return () => clearTimeout(timer);
    }
  }, [nodeId]);

  const node = nodes.find((n) => n.id === currentNodeId);

  // Load cached execution output when navigating to a node
  // Don't update if currently testing to preserve output for overlay
  useEffect(() => {
    if (isTesting) return; // Don't clear output while testing
    
    if (node?.data?.executionOutput) {
      setTestOutput(node.data.executionOutput);
      setTestError(null);
    } else {
      setTestOutput(null);
      setTestError(null);
    }
  }, [currentNodeId, node?.data?.executionOutput, isTesting]);

  useEffect(() => {
    if (isOpen) {
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    // Handle Escape key
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Memoize workflow graph structure separately (only recalculates when structure changes)
  // This is the key optimization - graph structure is cached and reused
  const workflowGraph = useMemo(() => {
    return workflowGraphService.getWorkflowGraph(workflowId, nodes, edges);
  }, [workflowId, nodes.length, edges.length]); // Only re-calc when node/edge COUNT changes

  // Create a lightweight hash of execution outputs to track changes without expensive JSON operations
  // Only track which nodes have outputs and a rough size indicator - avoid JSON.stringify on large data
  const executionOutputsHash = useMemo(() => {
    return nodes
      .filter(n => n.data?.executionOutput)
      .map(n => {
        const output = n.data.executionOutput;
        // Use lightweight indicators instead of JSON.stringify which can crash on large data
        const sizeHint = Array.isArray(output?.output)
          ? output.output.length
          : (typeof output?.output === 'object' ? Object.keys(output.output || {}).length : 0);
        return `${n.id}:${sizeHint}`;
      })
      .join('|');
  }, [nodes]);

  // Get input data from previous nodes using cached graph structure
  // This is now much faster because graph traversal is cached
  // Helper function to check if a node is upstream of the current node
  const isNodeUpstream = useCallback((sourceNodeId: string, targetNodeId: string): boolean => {
    const visited = new Set<string>();
    const queue: string[] = [targetNodeId];
    
    while (queue.length > 0) {
      const current = queue.shift()!;
      if (visited.has(current)) continue;
      visited.add(current);
      
      // Find all edges that lead TO the current node
      const incomingEdges = edges.filter(e => e.target === current);
      for (const edge of incomingEdges) {
        if (edge.source === sourceNodeId) {
          return true; // Found the source node upstream
        }
        queue.push(edge.source);
      }
    }
    
    return false;
  }, [edges]);

  const inputData = useMemo(() => {
    if (!node) return [];
    
    
    // ✅ Use backend's targetInputData if available (most accurate)
    // This is stored in node.data.cachedInputData and persists across modal open/close
    if (node.data?.cachedInputData && node.data.cachedInputData.length > 0) {
      
      // ✅ Filter to only show nodes that are actually upstream of the current node
      const filteredData = node.data.cachedInputData.filter((input: any) => {
        // Always include the current node's own data
        if (input.nodeId === currentNodeId) {
          return true;
        }
        // Only include nodes that are upstream
        const isUpstream = isNodeUpstream(input.nodeId, currentNodeId);
        
        if (!isUpstream) return false;
        
        // For loop nodes with specific handles, check if that handle is connected to the path
        if (input.sourceHandle) {
          const sourceNode = nodes.find((n: any) => n.id === input.nodeId);
          if (sourceNode?.type === 'loop') {
            // Check if there's an edge from this specific handle to a node in the path to current node
            const hasEdgeFromHandle = edges.some((edge: any) => 
              edge.source === input.nodeId && 
              edge.sourceHandle === input.sourceHandle &&
              isNodeUpstream(edge.target, currentNodeId)
            );
            
            // If this handle is NOT in the path, check if we're on the done path
            // If we're on the done path, we should show both loop and done handles
            if (!hasEdgeFromHandle && input.sourceHandle === 'loop') {
              // Check if the done handle is in the path to current node
              const doneHandleInPath = edges.some((edge: any) => 
                edge.source === input.nodeId && 
                edge.sourceHandle === 'done' &&
                isNodeUpstream(edge.target, currentNodeId)
              );
              return doneHandleInPath; // Show base loop output if we're on the done path
            }
            
            return hasEdgeFromHandle;
          }
        }
        
        return true;
      });
      
      return filteredData;
    }
    
    // Fallback: Use the optimized service method to re-compute from graph
    // Pass fresh nodes array to get current execution outputs
    const result = workflowGraphService.getInputDataForNode(currentNodeId, workflowGraph, edges, nodes);
    return result;
  }, [currentNodeId, workflowGraph, edges, nodes, refreshKey, executionOutputsHash, node?.data?.cachedInputData, isNodeUpstream]); // Track backend data changes

  // Normalize inputData: both data sources (cachedInputData and workflowGraphService)
  // provide `data` in unified format { success, output, logs, metrics }, but downstream
  // field components (StringField, NumberField) expect an `output` property.
  // Set `output` = unified format; expression evaluators unwrap the inner output.
  const enhancedInputData = useMemo(() => inputData.map((item: any) => {
    if (item.output !== undefined) return item;
    return { ...item, output: item.data };
  }), [inputData]);

  // Debug: Track inputData changes
  useEffect(() => {
  }, [enhancedInputData, isExecutingPrevious, isTesting]);

  // Clear cache when workflow changes or component unmounts
  useEffect(() => {
    return () => {
      workflowGraphService.clearCache();
    };
  }, [workflowId]);

  // Clear cached input data when edges change (workflow structure changed)
  // This ensures we don't show stale data after reconnecting nodes
  useEffect(() => {
    if (node?.data?.cachedInputData) {
      updateNode(currentNodeId, {
        cachedInputData: null
      });
    }
  }, [edges.length]); // Only track edges length to avoid excessive updates

  if (!isOpen || !node) {
    return null;
  }

  const handleClose = () => {
    // Clear test output when closing to free memory
    // Note: We DON'T clear node.data.cachedInputData because it should persist
    setTestOutput(null);
    setTestError(null);
    onClose();
  };

  const handleSave = () => {
    toast.success('Node configuration saved');
    handleClose();
  };



  const handleUpdateNode = (field: string, value: unknown) => {
    console.log('🔍 [NodeEditorModal] handleUpdateNode called:', {
      field,
      value: typeof value === 'string' && value.startsWith('whk_') ? value.substring(0, 10) + '...' : value,
      nodeId: currentNodeId
    });

    // Read LATEST node state from Zustand store directly (not from stale React closure).
    // This prevents a bug where rapid sequential calls (e.g., FileUploadField setting
    // contentBase64 + fileName + mimeType) would overwrite each other because React 18
    // batches updates and the `nodes` closure is stale between calls.
    const latestNodes = useWorkflowStore.getState().nodes;
    const currentNode = latestNodes.find((n: any) => n.id === currentNodeId);
    const currentParameters = currentNode?.data?.parameters || {};

    console.log('🔍 [NodeEditorModal] Current parameters before update:', {
      currentParameters: Object.keys(currentParameters),
      hasApiKey: !!currentParameters.apiKey
    });

    // Special handling for fields that belong at data level (not in parameters)
    // Note: toolName and toolDescription are MCP trigger properties and should be in parameters
    const dataLevelFields = ['label', 'toolDescriptionMode'];

    if (dataLevelFields.includes(field)) {
      // Update at data level
      console.log('🔍 [NodeEditorModal] Updating data level field:', field);
      updateNode(currentNodeId, {
        [field]: value
      });
    } else {
      // Update parameters object with new value
      const newParameters = {
        ...currentParameters,
        [field]: value
      };
      console.log('🔍 [NodeEditorModal] Updating parameters:', {
        field,
        hasApiKey: !!newParameters.apiKey,
        apiKeyValue: newParameters.apiKey ? newParameters.apiKey.substring(0, 10) + '...' : undefined
      });
      updateNode(currentNodeId, {
        parameters: newParameters
      });

      console.log('✅ [NodeEditorModal] updateNode called');
    }
  };

  // Batch update handler for updating multiple parameters at once
  // This avoids race conditions when multiple parameters need to be updated simultaneously
  const handleBatchUpdateNode = (updates: Record<string, unknown>) => {
    console.log('🔍 [NodeEditorModal] handleBatchUpdateNode called:', {
      updates,
      nodeId: currentNodeId
    });

    const currentNode = nodes.find(n => n.id === currentNodeId);
    const currentParameters = currentNode?.data?.parameters || {};

    // Merge all updates into parameters at once
    const newParameters = {
      ...currentParameters,
      ...updates
    };

    console.log('🔍 [NodeEditorModal] Batch updating parameters:', Object.keys(updates));
    updateNode(currentNodeId, {
      parameters: newParameters
    });

    console.log('✅ [NodeEditorModal] Batch updateNode called');
  };

  const handleNavigate = (targetNodeId: string) => {
    // Reset test state when navigating
    // Note: cachedInputData is stored in node.data and will be cleared by the edges useEffect
    setTestOutput(null);
    setTestError(null);
    setIsTesting(false);
    
    // Update current node
    setCurrentNodeId(targetNodeId);
    
    // Call external navigation handler if provided
    if (onNavigate) {
      onNavigate(targetNodeId);
    }
    
    toast.success('Navigated to node');
  };

  // Check if previous node is a trigger node
  const isPreviousNodeTrigger = () => {
    const previousNodes = edges
      .filter((edge) => edge.target === currentNodeId)
      .map((edge) => nodes.find((n) => n.id === edge.source))
      .filter(Boolean);

    // Check if any previous node is a trigger (category is 'trigger')
    // Also check the node type definition to be sure
    return previousNodes.some((prevNode) => {
      if (!prevNode) return false;
      
      // Check node data category
      if (prevNode.data?.category === 'trigger') return true;
      
      // Also check node definition
      const nodeDefinition = getNodeDefinition(prevNode.type || '');
      return nodeDefinition.category === 'triggers';
    });
  };

  const hasPreviousTrigger = isPreviousNodeTrigger();

  // Unified execution handler for both "Test" and "Execute Previous" buttons
  // - Test button (includeTarget=true): Uses testNode endpoint for fast isolated testing
  // - Execute Previous button (includeTarget=false): Uses execute-up-to endpoint to run workflow and populate inputs
  const handleExecutePrevious = async (includeTarget: boolean = false) => {
    // Set appropriate loading state based on which button was clicked
    if (includeTarget) {
      setIsTesting(true);
      setTestOutput(null); // Clear previous output
      setTestError(null);
      // Clear the persisted executionOutput in node data
      updateNode(currentNodeId, {
        executionOutput: null
      });
    } else {
      setIsExecutingPrevious(true);
    }

    try {
      // ✅ UNIFIED: Use ExecuteUpTo endpoint with "isolatedTest" mode for Test button
      // This consolidates all testing through a single endpoint
      if (includeTarget) {
        toast.info('Testing node...');

        // Convert inputData to the format expected by ExecuteUpTo endpoint
        // InputData is array of {nodeId, nodeName, nodeType, data, sourceHandle}
        // We need to convert to previousNodeOutputs object: {nodeLabel: data}
        const previousNodeOutputs: Record<string, any> = {};
        inputData.forEach((input: any) => {
          const nodeName = input.nodeName || input.nodeId;
          // Pass the full data structure (unified format) so interpolation can access nested properties
          previousNodeOutputs[nodeName] = input.data;
        });

        // ✅ FIX: Include workflow definition so AI Agent nodes can access their sub-nodes (tools, memory, parser, chatModel)
        // Clean the workflow data to remove React Flow internals
        const cleanedNodes = nodes.map(n => {
          const { icon, component, ...cleanData } = n.data || {};
          return {
            id: n.id,
            type: n.type,
            position: n.position,
            data: cleanData,
          };
        });

        const cleanedEdges = edges.map(e => ({
          id: e.id,
          source: e.source,
          target: e.target,
          sourceHandle: e.sourceHandle,
          targetHandle: e.targetHandle,
        }));

        const workflowDefinition = {
          nodes: cleanedNodes,
          edges: cleanedEdges,
        };

        // Call ExecuteUpTo endpoint with isolatedTest mode
        const response = await workflowApi.executeUpTo(
          workflowId,
          currentNodeId,
          workflowDefinition,
          false, // includeTarget (backwards compat, will be overridden by mode)
          'isolatedTest', // mode
          previousNodeOutputs // previousNodeOutputs
        );
        const result = response.data;

        if (!result.success) {
          throw new Error(result.error || 'Node test failed');
        }

        // Extract the target node output from nodeOutputs
        const targetNodeOutput = result.nodeOutputs?.[currentNodeId];

        if (!targetNodeOutput) {
          throw new Error('No output returned for target node');
        }

        // Update node with execution output - store in unified format for consistency with Execute Previous
        // targetNodeOutput is already in unified format: { success, output, logs, metrics }
        updateNode(currentNodeId, {
          executionOutput: targetNodeOutput, // Store the full unified format
          sourceNodeType: node.type,
          sourceNodeName: node.data?.label || node.type
        });

        // Pass the target node output to the UI in unified format
        // The UI expects: { success, output, error, logs, metrics, executionTime }
        setTestOutput(targetNodeOutput);
        toast.success('Node tested successfully');

        return; // Exit early for Test button flow
      }

      // ⬇️ EXISTING: Execute Previous button flow continues below

      // 🧹 Clear test execution cache from database before starting new test
      // This ensures we start with a clean slate and don't see old cached data
      await executionApi.clearTestCache(workflowId);

      // ✅ Clear ALL cached input data from all nodes before starting new execution
      nodes.forEach(n => {
        if (n.data?.cachedInputData) {
          updateNode(n.id, {
            cachedInputData: null
          });
        }
      });

      toast.info('Executing previous nodes...');
      
      // Include current workflow definition for unsaved changes
      // Clean the data to remove any circular references or non-serializable objects
      // Use a deep serialization approach to catch all circular references
      const cleanWorkflowDefinition = () => {
        // Remove React Flow internal properties and large execution artifacts
        const cleanedNodes = nodes.map(n => {
          // Destructure to remove React Flow internal properties and execution data
          // Handle case where n.data might be undefined
          // Exclude: icon, component, cachedInputData, _lastOutput, _executionResult
          // These can contain large binary data from previous executions
          const {
            icon,
            component,
            cachedInputData,
            _lastOutput,
            _executionResult,
            _handles,
            ...cleanData
          } = n.data || {};

          return {
            id: n.id,
            type: n.type,
            position: n.position,
            data: cleanData, // Keep config data, exclude execution artifacts
          };
        });

        const cleanedEdges = edges.map(e => ({
          id: e.id,
          source: e.source,
          target: e.target,
          sourceHandle: e.sourceHandle,
          targetHandle: e.targetHandle,
        }));

        return {
          nodes: cleanedNodes,
          edges: cleanedEdges,
        };
      };
      
      const workflowDefinition = cleanWorkflowDefinition();
      
      
      // Call execute-up-to endpoint with includeTarget flag
      // includeTarget: true = Execute up to AND including the target node (Test mode)
      // includeTarget: false = Execute up to (but NOT including) the target node (Execute Previous mode)
      const requestBody = {
        targetNodeId: currentNodeId,
        includeTarget, // Passed as parameter
        workflowDefinition, // Include current workflow state for unsaved changes
      };
      
      const response = await workflowApi.executeUpTo(
        workflowId,
        currentNodeId,
        workflowDefinition,
        includeTarget
      );
      const result = response.data;

      if (!result.success) {
        throw new Error(result.error || 'Execution failed');
      }

      // ✅ Store backend's targetInputData in the node's data for persistence
      // This ensures the data survives modal close/reopen
      if (result.targetInputData && result.targetInputData.length > 0) {
        // console.log('[handleExecutePrevious] targetInputData structure:', result.targetInputData.map((item: any) => ({
        //   nodeId: item.nodeId,
        //   nodeName: item.nodeName,
        //   nodeType: item.nodeType,
        //   sourceHandle: item.sourceHandle,
        //   hasNodeType: !!item.nodeType
        // })));
        updateNode(currentNodeId, {
          cachedInputData: result.targetInputData
        });
      }

      // Backend now returns unified format with all node outputs for both modes
      // Format: { success: true, nodeOutputs: {...}, executedNodes: [...], logs: [], metrics: {...} }
      // Ensure nodeOutputs is always an object
      const nodeOutputs = result.nodeOutputs || {};
      // console.log('[handleExecutePrevious] nodeOutputs after fallback:', {
      //   type: typeof nodeOutputs,
      //   isObject: typeof nodeOutputs === 'object',
      //   keys: Object.keys(nodeOutputs),
      //   keysCount: Object.keys(nodeOutputs).length
      // });
      
      if (nodeOutputs && Object.keys(nodeOutputs).length > 0) {
        // When includeTarget is false, backend returns all node outputs
        // Format: { success: true, nodeOutputs: {...}, executedNodes: [...] }
        
        // First pass: Accumulate all handle-specific outputs and regular outputs
        const handleOutputs: Record<string, Record<string, any>> = {};
        const regularOutputs: Record<string, any> = {};
        
        Object.entries(result.nodeOutputs).forEach(([key, output]) => {
          if (key.includes(':')) {
            // Handle-specific output (e.g., "loop-node-id:loop")
            const [nodeId, handle] = key.split(':');
            
            if (!handleOutputs[nodeId]) {
              handleOutputs[nodeId] = {};
            }
            // Extract the actual data from the response wrapper
            // Backend sends: { success: true, output: {...}, logs: [], metrics: {...} }
            // We want just the output part for _handles
            const handleData = (output as any)?.output || output;
            handleOutputs[nodeId][handle] = handleData;
          } else {
            // Regular node output
            const node = nodes.find(n => n.id === key);
            if (node) {
              regularOutputs[key] = output;
            } else {
            }
          }
        });
        
        // Second pass: Update each node ONCE with all its data
        const nodesToUpdate = new Set([...Object.keys(handleOutputs), ...Object.keys(regularOutputs)]);
        
        nodesToUpdate.forEach(nodeId => {
          const targetNode = nodes.find(n => n.id === nodeId);
          if (!targetNode) return;
          
          const currentOutput = targetNode.data?.executionOutput || {};
          const handles = handleOutputs[nodeId];
          const regularOutput = regularOutputs[nodeId];
          const isLoopNode = targetNode.type === 'loop';
          
          // For Loop nodes with handle outputs, store ALL handles in _handles
          // For other nodes, merge handles into _handles
          let newExecutionOutput;
          if (isLoopNode && handles) {
            // For loop nodes, use the regular output as base and store ALL handles in _handles
            newExecutionOutput = {
              ...(regularOutput || currentOutput),
              _handles: {
                ...(currentOutput._handles || {}),
                ...handles  // This includes both 'loop' and 'done' handles
              }
            };
          } else {
            // For other nodes, merge regular output with handles under _handles
            newExecutionOutput = {
              ...(regularOutput || currentOutput),
              ...(handles && {
                _handles: {
                  ...(currentOutput._handles || {}),
                  ...handles
                }
              })
            };
          }
          
          // console.log(`[handleExecutePrevious] Updating node ${nodeId}:`, {
          //   hasRegularOutput: !!regularOutput,
          //   handles: handles ? Object.keys(handles) : [],
          //   newExecutionOutput
          // });
          
          // Store metadata about the source node for Output Panel icon display
          updateNode(nodeId, { 
            executionOutput: newExecutionOutput,
            sourceNodeType: targetNode.type,
            sourceNodeName: targetNode.data?.label || targetNode.type
          });
        });
        
        // Force a re-render to update the input panel
        setRefreshKey(prev => prev + 1);
        
        // If includeTarget is true (Test mode), set the test output for the current node
        // If the result doesn't have output for current node, keep the existing output (for overlay)
        if (includeTarget) {
          if (result.nodeOutputs[currentNodeId]) {
            setTestOutput(result.nodeOutputs[currentNodeId]);
          }
          // If no output returned, keep existing testOutput (don't clear it)
        }
      }

      const executedCount = result.executedNodes?.length || 0;
      toast.success(`Executed ${executedCount} nodes successfully`);
    } catch (error: any) {
      // Extract the actual backend error message
      const errorMessage = error.response?.data?.error ||
                          error.response?.data?.message ||
                          error.message ||
                          'Execution failed';

      console.error('[NodeEditor] Execute previous failed:', {
        backendError: error.response?.data,
        errorMessage,
        fullError: error
      });

      // Clear old output when execution fails
      if (includeTarget) {
        setTestOutput(null);
        setTestError(errorMessage);
        // Also clear the persisted executionOutput in node data
        updateNode(currentNodeId, {
          executionOutput: null
        });
      }

      // Show appropriate toast message based on which operation failed
      const toastMessage = includeTarget
        ? `Test failed: ${errorMessage}`
        : `Failed to execute previous nodes: ${errorMessage}`;
      toast.error(toastMessage);
    } finally {
      // Reset the appropriate loading state
      if (includeTarget) {
        setIsTesting(false);
      } else {
        setIsExecutingPrevious(false);
      }
    }
  };

  // Get node definition for icon and color
  const nodeDefinition = getNodeDefinition(node.type || 'default');
  const NodeIcon = nodeDefinition?.icon;

  // Check if there's previous input data available
  const hasPreviousInputData = inputData.length > 0;
  
  // Check if this is the first node (no incoming edges)
  const isFirstNode = edges.filter((edge) => edge.target === currentNodeId).length === 0;

  // Check if this node is a sub-node (connected to AI Agent via anchor)
  const isSubNode = NodeNavigationService.isSubNode(currentNodeId, edges);

  return (
    <NodeEditorErrorBoundary 
      nodeName={node?.data?.label || node?.type} 
      onClose={onClose}
    >
      <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm animate-in fade-in duration-200">
        {/* Modal Container - 95% screen coverage */}
        <div className="absolute inset-[2.5%] bg-background rounded-lg shadow-2xl border flex flex-col overflow-hidden">
        {/* Close Button - Top Right */}
        <Button
          variant="ghost"
          size="icon"
          onClick={handleClose}
          className="absolute top-4 right-4 z-10 h-8 w-8"
        >
          <X className="h-4 w-4" />
        </Button>

        {/* Three-Panel Layout with Resizers */}
        <div className="flex-1 overflow-hidden">
          <ResizablePanelGroup direction="horizontal">
            {/* Left Panel - Input Data */}
            <ResizablePanel defaultSize={33} minSize={20} maxSize={50}>
              <div className="h-full flex flex-col bg-muted/50 relative overflow-hidden">
                <div className="p-3 border-b bg-muted/80 flex-shrink-0">
                  <h3 className="font-semibold text-xs">Input Data</h3>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    From previous nodes
                  </p>
                </div>
                <div className="flex-1 overflow-hidden">
                  {isInitializing ? (
                    <InputPanelSkeleton />
                  ) : (
                    <NodeInputPanel
                      inputData={enhancedInputData}
                      onExecutePrevious={isSubNode ? undefined : handleExecutePrevious}
                      isExecuting={isExecutingPrevious}
                      isPreviousNodeTrigger={isPreviousNodeTrigger()}
                      isFirstNode={isFirstNode}
                      currentNodeId={currentNodeId}
                    />
                  )}
                </div>
              </div>
            </ResizablePanel>

            <ResizableHandle withHandle />

            {/* Center Panel - Parameters */}
            <ResizablePanel defaultSize={34} minSize={30} maxSize={60}>
              <div className="h-full flex flex-col bg-background overflow-hidden">
                {/* Parameters Header with Icon, Label, and Test Button */}
                <div className="p-6 border-b flex items-center justify-between flex-shrink-0">
                  <div className="flex items-center gap-4">
                    {/* Node Icon with color */}
                    <div 
                      className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ 
                        backgroundColor: nodeDefinition?.bgColor || '#f3f4f6',
                        borderColor: nodeDefinition?.borderColor || '#d1d5db',
                        borderWidth: '2px'
                      }}
                    >
                      {NodeIcon && (
                        <NodeIcon 
                          className="h-6 w-6" 
                          style={{ color: nodeDefinition?.color || '#6b7280' }}
                        />
                      )}
                    </div>
                    {/* Node Label and Description */}
                    <div>
                      <h3 className="font-semibold text-lg">{node.data.label || node.type}</h3>
                      <p className="text-sm text-muted-foreground mt-0.5">
                        Configure node settings
                      </p>
                    </div>
                  </div>
                  {/* Test Button - Hidden for sub-nodes (model, memory, tool, parser) */}
                  {!isSubNode && (
                    <Button
                      size="default"
                      onClick={() => handleExecutePrevious(true)}
                      disabled={isTesting || (!isFirstNode && !hasPreviousInputData && !hasPreviousTrigger)}
                      className="gap-2"
                      title={!isFirstNode && !hasPreviousInputData && !hasPreviousTrigger ? 'No previous input data available. Execute previous nodes first.' : ''}
                    >
                      <Play className="h-4 w-4" />
                      {isTesting ? 'Testing...' : 'Test'}
                    </Button>
                  )}
                </div>
                
                {/* Parameters Content */}
                <div className="flex-1 overflow-hidden">
                  {isInitializing ? (
                    <PropertyPanelSkeleton />
                  ) : (
                    <DynamicNodeParametersPanel
                      node={node}
                      onUpdate={handleUpdateNode}
                      onBatchUpdate={handleBatchUpdateNode}
                      nodes={nodes}
                      edges={edges}
                      inputData={enhancedInputData}
                    />
                  )}
                </div>
              </div>
            </ResizablePanel>

            <ResizableHandle withHandle />

            {/* Right Panel - Output Preview */}
            <ResizablePanel defaultSize={33} minSize={20} maxSize={50}>
              <div className="h-full flex flex-col bg-muted/50 relative overflow-hidden">
                <div className="p-3 border-b bg-muted/80 flex-shrink-0">
                  <h3 className="font-semibold text-xs">Output</h3>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    {testOutput ? 'Test results' : 'Test results'}
                  </p>
                </div>
                <div className="flex-1 overflow-hidden">
                  {isInitializing ? (
                    <OutputPanelSkeleton />
                  ) : (
                    <NodeOutputPanel
                      output={testOutput || null}
                      error={testError}
                      isLoading={isTesting}
                      nodeType={node?.data?.sourceNodeType || node?.data?.type}
                      nodeName={node?.data?.sourceNodeName || node?.data?.label}
                    />
                  )}
                </div>
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>
        </div>

        {/* Floating Navigation Buttons */}
        <NavigationButtons
          currentNodeId={currentNodeId}
          nodes={nodes}
          edges={edges}
          onNavigate={handleNavigate}
          floating
        />

        {/* Sub-Node Navigation Buttons (for AI Agent and its sub-nodes) */}
        <SubNodeNavigationButtons
          currentNodeId={currentNodeId}
          nodes={nodes}
          edges={edges}
          onNavigate={handleNavigate}
        />
        </div>
      </div>
    </NodeEditorErrorBoundary>
  );
}
